import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const TYPE_LABELS = {
  closure: "Jours fériés & fermetures",
  holiday: "Jours fériés & fermetures",
  family_saturday: "Journées parents-enfants",
  thematic: "Journées thématiques",
  summer: "Horaire d'été",
};
const TYPE_ORDER = ["closure", "holiday", "summer", "family_saturday", "thematic"];

const RULE_BADGE = {
  closed: { label: "Fermé", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  special_hours: { label: "Horaire spécial", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  themed: { label: "Thématique", cls: "bg-violet-500/15 text-violet-300 border-violet-500/30" },
  event: { label: "Événement", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  normal: { label: "Normal", cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" },
};

export default function SpecialDaysModule() {
  const [tab, setTab] = useState("calendrier");
  const year = new Date().getFullYear();
  const [calendar, setCalendar] = useState(null);
  const [birthdays, setBirthdays] = useState(null);
  const [perks, setPerks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reminders, setReminders] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapKind, setSnapKind] = useState("rentree");
  const [snapPreview, setSnapPreview] = useState(null);
  const [journal, setJournal] = useState(null);
  const [optouts, setOptouts] = useState([]);
  const [newOptout, setNewOptout] = useState("");

  const loadCalendar = async () => {
    try { const r = await api.get(`/special-days/calendar?year=${year}`); setCalendar(r.data); } catch {}
  };
  const loadBirthdays = async () => {
    try {
      const [b, p] = await Promise.all([
        api.get("/special-days/birthdays?days=60"),
        api.get("/special-days/birthdays/perks"),
      ]);
      setBirthdays(b.data); setPerks(p.data.perks || []);
    } catch {}
  };
  const loadSettings = async () => {
    try { const r = await api.get("/special-days/settings"); setSettings(r.data); } catch {}
  };

  useEffect(() => { loadCalendar(); loadSettings(); }, []);
  useEffect(() => { if (tab === "anniversaires") loadBirthdays(); }, [tab]);
  useEffect(() => { if (tab === "communication") loadCommunication(); }, [tab]);
  useEffect(() => { if (tab === "journal") loadJournal(); }, [tab]);

  const loadJournal = async () => {
    try {
      const [j, o] = await Promise.all([
        api.get("/special-days/communications"),
        api.get("/special-days/optouts"),
      ]);
      setJournal(j.data); setOptouts(o.data.optouts || []);
    } catch {}
  };
  const addOptout = async () => {
    if (!newOptout.trim()) return;
    try {
      await api.post("/special-days/optouts", { contact: newOptout.trim() });
      setNewOptout("");
      toast.success("Désinscription ajoutée");
      await loadJournal();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeOptout = async (oid) => {
    try { await api.delete(`/special-days/optouts/${oid}`); toast.success("Réinscrit"); await loadJournal(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const publishDay = async (sid, name) => {
    if (!window.confirm(`Publier « ${name} » sur la page Facebook du dojo ?`)) return;
    try {
      const r = await api.post(`/special-days/${sid}/publish-social`, { networks: ["facebook"] });
      const fb = r.data.results?.facebook;
      if (fb?.ok) toast.success("📱 Publié sur Facebook !");
      else toast.error(`Facebook: ${fb?.error || "échec"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur publication"); }
  };

  const loadCommunication = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/special-days/reminders/upcoming?days=120"),
        api.get("/special-days/snapshots"),
      ]);
      setReminders(r.data); setSnapshots(s.data.snapshots || []);
    } catch {}
  };
  const sendReminder = async (sid, name) => {
    if (!window.confirm(`Envoyer le rappel « ${name} » aux parents (élèves actifs) maintenant ?`)) return;
    try {
      const r = await api.post(`/special-days/reminders/send/${sid}`, {});
      const em = r.result?.email || r.email;
      toast.success(`📣 Rappel envoyé${em ? ` · ${em.sent || 0} emails` : ""}`);
      await loadCommunication();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi"); }
  };
  const generateSnapshot = async (sendEmail) => {
    if (sendEmail && !window.confirm("Générer ET emailer ce snapshot à tous les parents actifs ?")) return;
    setBusy(true);
    try {
      const r = await api.post("/special-days/snapshots/generate", { kind: snapKind, year, send_email: sendEmail });
      setSnapPreview(r.data);
      toast.success(sendEmail ? `📧 Snapshot généré + envoyé (${r.data.email_result?.sent || 0} emails)` : "✅ Snapshot généré");
      await loadCommunication();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur snapshot"); }
    finally { setBusy(false); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/special-days/generate?year=${year}`);
      toast.success(`✅ Calendrier ${year} généré (${r.data.total} journées)`);
      await loadCalendar();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur génération"); }
    finally { setBusy(false); }
  };

  const issueBirthday = async (cid, send) => {
    try {
      const r = await api.post("/special-days/birthdays/issue", { student_contract_id: cid, send });
      if (r.data.already_issued) toast.info("Avantage déjà émis cette année");
      else toast.success(send ? "🎁 Avantage émis + envoyé (SMS/email)" : "🎁 Avantage émis (sans envoi)");
      await loadBirthdays();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const markUsed = async (pid) => {
    try { await api.post(`/special-days/birthdays/${pid}/mark-used`); toast.success("Marqué utilisé"); await loadBirthdays(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.put("/special-days/settings", settings);
      toast.success("✅ Réglages enregistrés");
      await loadSettings();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="special-days-module">
      {/* Header */}
      <div className="border border-zinc-800 rounded-2xl p-6 bg-gradient-to-br from-zinc-950 to-black">
        <div className="text-amber-400 text-[10px] tracking-[0.4em] mb-2">CALENDRIER KAIZEN · JOURNÉES SPÉCIALES</div>
        <h1 className="text-3xl font-bold tracking-tight">Calendrier Kaizen & Journées Spéciales</h1>
        <p className="text-zinc-500 text-sm mt-2">
          L'année vue comme un système vivant : jours fériés, anniversaires, mode été, journées parents-enfants — automatisés, sans chaos.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 flex-wrap" data-testid="special-days-tabs">
        <TabBtn active={tab === "calendrier"} onClick={() => setTab("calendrier")} testid="tab-calendrier">📅 Calendrier {year}</TabBtn>
        <TabBtn active={tab === "anniversaires"} onClick={() => setTab("anniversaires")} testid="tab-anniversaires">🎂 Anniversaires & avantages</TabBtn>
        <TabBtn active={tab === "communication"} onClick={() => setTab("communication")} testid="tab-communication">📣 Rappels & Snapshots</TabBtn>
        <TabBtn active={tab === "journal"} onClick={() => setTab("journal")} testid="tab-journal">📊 Journal des envois</TabBtn>
        <TabBtn active={tab === "reglages"} onClick={() => setTab("reglages")} testid="tab-reglages">⚙️ Réglages</TabBtn>
      </div>

      {/* CALENDRIER */}
      {tab === "calendrier" && (
        <div className="space-y-4" data-testid="calendar-panel">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex gap-2 flex-wrap">
              {calendar && Object.entries(calendar.counts).map(([t, n]) => (
                <Badge key={t} variant="outline" className="text-[11px] text-zinc-300 border-zinc-700 bg-zinc-900/60">{TYPE_LABELS[t] || t}: {n}</Badge>
              ))}
            </div>
            <Button onClick={generate} disabled={busy} size="sm" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs" data-testid="generate-calendar-btn">
              {busy ? "…" : `↻ Générer le calendrier ${year}`}
            </Button>
          </div>

          {!calendar || calendar.total === 0 ? (
            <div className="text-center text-zinc-500 py-16 border border-zinc-800 rounded-2xl">
              <div className="text-4xl mb-3">🗓️</div>
              <div className="text-sm">Aucune journée générée pour {year}.</div>
              <div className="text-xs text-zinc-600 mt-1">Clique « Générer le calendrier {year} ».</div>
            </div>
          ) : (
            TYPE_ORDER.filter((t) => calendar.days.some((d) => d.type === t)).map((t) => (
              <div key={t} className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40" data-testid={`section-${t}`}>
                <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">{TYPE_LABELS[t]}</div>
                <div className="space-y-2">
                  {calendar.days.filter((d) => d.type === t).map((d) => {
                    const rb = RULE_BADGE[d.rule] || RULE_BADGE.normal;
                    return (
                      <div key={d.id} className="border border-zinc-800 rounded-lg p-3 bg-black/30" data-testid={`day-${d.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono text-amber-300">{new Date(d.date + "T12:00:00").toLocaleDateString("fr-CA", { weekday: "short", day: "2-digit", month: "short" })}</span>
                            <span className="text-sm font-semibold text-zinc-100">{d.name}</span>
                            {d.overridden && <Badge variant="outline" className="text-[9px] text-amber-400">modifié</Badge>}
                          </div>
                          <Badge variant="outline" className={`text-[10px] ${rb.cls}`}>{rb.label}</Badge>
                        </div>
                        <div className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{d.message_fr}</div>
                        <div className="mt-2">
                          <Button size="sm" variant="outline" onClick={() => publishDay(d.id, d.name)} className="text-[10px] h-7" data-testid={`publish-day-${d.id}`}>📱 Publier sur Facebook</Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ANNIVERSAIRES */}
      {tab === "anniversaires" && (
        <div className="grid lg:grid-cols-3 gap-4" data-testid="birthdays-panel">
          <div className="lg:col-span-2 space-y-2">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">À venir (60 jours)</div>
            {!birthdays ? (
              <div className="text-sm text-zinc-500 italic">Chargement…</div>
            ) : birthdays.count === 0 ? (
              <div className="text-sm text-zinc-500 italic">Aucun anniversaire dans les 60 prochains jours.</div>
            ) : birthdays.birthdays.map((b) => (
              <div key={b.student_contract_id} className={`border rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap ${b.is_today ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-800 bg-zinc-950/40"}`} data-testid={`birthday-${b.student_contract_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="text-center w-12">
                    <div className="text-base font-bold text-amber-300">{b.is_today ? "🎂" : b.days_until + "j"}</div>
                    <div className="text-[9px] text-zinc-500">{new Date(b.birthday_date + "T12:00:00").toLocaleDateString("fr-CA", { day: "2-digit", month: "short" })}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{b.full_name} {b.age_turning ? <span className="text-zinc-500 font-normal">· {b.age_turning} ans</span> : null}</div>
                    <div className="text-[10px] text-zinc-500 flex gap-2">
                      <span>{b.has_phone ? "📱 SMS" : "📵 pas de tél"}</span>
                      <span>{b.has_email ? "✉️ email" : "🚫 pas d'email"}</span>
                      {b.perk_status && <span className="text-emerald-400">avantage: {b.perk_status}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  {!b.perk_status ? (
                    <>
                      <Button size="sm" onClick={() => issueBirthday(b.student_contract_id, true)} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[11px]" data-testid={`issue-send-${b.student_contract_id}`}>🎁 Émettre + Envoyer</Button>
                      <Button size="sm" variant="outline" onClick={() => issueBirthday(b.student_contract_id, false)} className="text-[11px]" data-testid={`issue-only-${b.student_contract_id}`}>Émettre seul</Button>
                    </>
                  ) : (
                    <Badge variant="outline" className="text-[10px] text-emerald-400">{b.perk_status}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Perks émis */}
          <div>
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Avantages émis ({year})</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="perks-list">
              {perks.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Aucun avantage émis.</div>
              ) : perks.map((p) => (
                <div key={p.id} className="border border-zinc-800 rounded-lg p-2.5 text-xs bg-zinc-950/40" data-testid={`perk-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-zinc-100 truncate">{p.first_name}</span>
                    <Badge variant="outline" className={`text-[9px] ${p.status === "used" ? "text-zinc-400" : p.status === "sent" ? "text-emerald-400" : p.status === "failed" ? "text-red-400" : "text-amber-400"}`}>{p.status}</Badge>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5">{p.benefit_label}</div>
                  <div className="text-[10px] text-zinc-600">valable → {p.valid_until}</div>
                  {p.status !== "used" && (
                    <Button size="sm" variant="outline" onClick={() => markUsed(p.id)} className="text-[10px] mt-1.5 h-6" data-testid={`mark-used-${p.id}`}>✓ Marquer utilisé</Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* COMMUNICATION — Rappels & Snapshots */}
      {tab === "communication" && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="communication-panel">
          {/* Rappels */}
          <div className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Rappels de fermeture à venir</div>
            <div className="text-[11px] text-zinc-600 mb-3">Envoi auto par email (cron 9h00) selon le délai de chaque journée. Tu peux aussi envoyer manuellement.</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="reminders-list">
              {!reminders ? (
                <div className="text-sm text-zinc-500 italic">Chargement…</div>
              ) : reminders.count === 0 ? (
                <div className="text-sm text-zinc-500 italic">Aucun rappel dans les 120 prochains jours.</div>
              ) : reminders.reminders.map((r) => (
                <div key={r.id} className={`border rounded-lg p-3 flex items-center justify-between gap-2 ${r.due_now ? "border-amber-500/40 bg-amber-500/10" : "border-zinc-800 bg-black/30"}`} data-testid={`reminder-${r.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-zinc-100 truncate">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">📅 {r.date} · rappel le {r.notify_date}</div>
                  </div>
                  {r.already_sent ? (
                    <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-500/30">envoyé</Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => sendReminder(r.id, r.name)} className="text-[11px]" data-testid={`send-reminder-${r.id}`}>📣 Envoyer</Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Snapshots */}
          <div className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Snapshots annuels</div>
            <div className="flex gap-2 items-center mb-3 flex-wrap">
              <select value={snapKind} onChange={(e) => setSnapKind(e.target.value)} className="bg-black border border-zinc-800 rounded-md text-sm p-2" data-testid="snapshot-kind">
                <option value="rentree">Rentrée (sept→juin)</option>
                <option value="mi_annee">Mi-année (jan→juin)</option>
                <option value="ete">Été (juin→août)</option>
              </select>
              <Button size="sm" onClick={() => generateSnapshot(false)} disabled={busy} variant="outline" className="text-xs" data-testid="gen-snapshot-btn">👁️ Générer (aperçu)</Button>
              <Button size="sm" onClick={() => generateSnapshot(true)} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 text-xs" data-testid="gen-send-snapshot-btn">📧 Générer + emailer</Button>
            </div>
            {snapPreview && (
              <div className="mb-3" data-testid="snapshot-preview">
                <pre className="text-xs text-zinc-100 whitespace-pre-wrap font-sans leading-relaxed bg-black/40 border border-zinc-800 rounded-lg p-3 max-h-[40vh] overflow-y-auto">{snapPreview.text}</pre>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(snapPreview.text); toast.success("📋 Copié"); }} className="text-[10px] mt-2" data-testid="copy-snapshot-btn">📋 Copier</Button>
              </div>
            )}
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1 mt-3">Historique</div>
            <div className="space-y-1.5 max-h-[25vh] overflow-y-auto">
              {snapshots.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Aucun snapshot généré.</div>
              ) : snapshots.map((s) => (
                <button key={s.id} onClick={() => setSnapPreview(s)} className="w-full text-left border border-zinc-800 bg-black/30 hover:border-zinc-600 rounded-lg p-2 text-xs transition" data-testid={`snapshot-${s.id}`}>
                  <span className="font-semibold text-zinc-100">{s.kind} · {s.year}</span>
                  <span className="text-[10px] text-zinc-500 float-right">{new Date(s.created_at).toLocaleDateString("fr-CA")}{s.email_result ? ` · ${s.email_result.sent || 0} emails` : ""}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* JOURNAL DES ENVOIS */}
      {tab === "journal" && (
        <div className="space-y-4" data-testid="journal-panel">
          {journal && (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <StatCard label="Anniversaires" value={journal.summary.anniversaire} icon="🎂" />
              <StatCard label="Rappels" value={journal.summary.rappel} icon="📣" />
              <StatCard label="Snapshots" value={journal.summary.snapshot} icon="📅" />
              <StatCard label="Destinataires" value={journal.summary.total_recipients} icon="👥" />
              <StatCard label="Désinscriptions" value={optouts.length} icon="🚫" />
            </div>
          )}
          <div className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-3">Historique des messages automatiques</div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="journal-list">
              {!journal ? (
                <div className="text-sm text-zinc-500 italic">Chargement…</div>
              ) : journal.count === 0 ? (
                <div className="text-sm text-zinc-500 italic">Aucun message envoyé pour le moment. Les anniversaires, rappels et snapshots apparaîtront ici.</div>
              ) : journal.events.map((e, i) => {
                const kindCls = e.kind === "anniversaire" ? "text-amber-300" : e.kind === "rappel" ? "text-sky-300" : "text-violet-300";
                const statusCls = e.status === "envoyé" ? "text-emerald-400 border-emerald-500/30" : e.status === "généré" ? "text-zinc-400 border-zinc-600" : "text-red-400 border-red-500/30";
                return (
                  <div key={`${e.at}-${i}`} className="border border-zinc-800 rounded-lg p-3 bg-black/30 flex items-center justify-between gap-2 flex-wrap" data-testid={`journal-event-${i}`}>
                    <div className="min-w-0">
                      <div className={`text-sm font-semibold ${kindCls} truncate`}>{e.label}</div>
                      <div className="text-[10px] text-zinc-500">
                        {e.at ? new Date(e.at).toLocaleString("fr-CA", { dateStyle: "medium", timeStyle: "short" }) : ""}
                        {e.channels.length > 0 && <span> · {e.channels.join(" + ")}</span>}
                        {e.recipients > 0 && <span> · {e.recipients} destinataire{e.recipients > 1 ? "s" : ""}</span>}
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] ${statusCls}`}>{e.status}</Badge>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Désinscriptions (conformité LCAP) */}
          <div className="border border-zinc-800 rounded-2xl p-4 bg-zinc-950/40" data-testid="optouts-panel">
            <div className="text-[10px] tracking-[0.3em] text-zinc-500 uppercase mb-1">Désinscriptions (conformité LCAP)</div>
            <div className="text-[11px] text-zinc-600 mb-3">Les contacts désinscrits sont automatiquement exclus de tous les envois. Lien de désinscription inclus dans chaque courriel · STOP géré par SMS.</div>
            <div className="flex gap-2 mb-3">
              <Input value={newOptout} onChange={(e) => setNewOptout(e.target.value)} placeholder="Email ou numéro à exclure manuellement" className="bg-black border-zinc-800 text-sm h-9" data-testid="new-optout-input" />
              <Button size="sm" onClick={addOptout} variant="outline" className="text-xs" data-testid="add-optout-btn">+ Exclure</Button>
            </div>
            <div className="space-y-1.5 max-h-[35vh] overflow-y-auto" data-testid="optouts-list">
              {optouts.length === 0 ? (
                <div className="text-xs text-zinc-500 italic">Aucune désinscription. 🎉</div>
              ) : optouts.map((o) => (
                <div key={o.id} className="border border-zinc-800 rounded-lg p-2.5 bg-black/30 flex items-center justify-between gap-2 text-xs" data-testid={`optout-${o.id}`}>
                  <div className="min-w-0">
                    <span className="font-mono text-zinc-200 truncate">{o.contact}</span>
                    <span className="text-[10px] text-zinc-500 ml-2">{o.channel} · {o.source}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => removeOptout(o.id)} className="text-[10px] h-6" data-testid={`resub-${o.id}`}>↩ Réinscrire</Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {tab === "reglages" && settings && (
        <div className="max-w-2xl space-y-5 border border-zinc-800 rounded-2xl p-6 bg-zinc-950/40" data-testid="settings-panel">
          {/* Auto-send */}
          <label className="flex items-center justify-between gap-3 cursor-pointer">
            <div>
              <div className="text-sm font-semibold text-zinc-100">Envoi automatique des anniversaires</div>
              <div className="text-xs text-zinc-500">SMS + email envoyés automatiquement le jour J (cron quotidien 8h15).</div>
            </div>
            <input type="checkbox" checked={!!settings.auto_send_birthday} onChange={(e) => setSettings({ ...settings, auto_send_birthday: e.target.checked })} className="w-5 h-5 accent-amber-500" data-testid="setting-auto-send" />
          </label>

          <div className="border-t border-zinc-800 pt-4">
            <div className="text-sm font-semibold text-zinc-100 mb-2">Avantage anniversaire</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Type</div>
                <select value={settings.birthday_benefit?.type || "percent"} onChange={(e) => setSettings({ ...settings, birthday_benefit: { ...settings.birthday_benefit, type: e.target.value } })} className="w-full bg-black border border-zinc-800 rounded-md text-sm p-2" data-testid="setting-benefit-type">
                  <option value="percent">Pourcentage (%)</option>
                  <option value="fixed">Montant fixe ($)</option>
                </select>
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Valeur</div>
                <Input type="number" value={settings.birthday_benefit?.value ?? 20} onChange={(e) => setSettings({ ...settings, birthday_benefit: { ...settings.birthday_benefit, value: Number(e.target.value) } })} className="bg-black border-zinc-800" data-testid="setting-benefit-value" />
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Cours privé (min)</div>
                <Input type="number" value={settings.birthday_benefit?.private_lesson_minutes ?? 45} onChange={(e) => setSettings({ ...settings, birthday_benefit: { ...settings.birthday_benefit, private_lesson_minutes: Number(e.target.value) } })} className="bg-black border-zinc-800" data-testid="setting-benefit-minutes" />
              </div>
              <div>
                <div className="text-[10px] text-zinc-500 uppercase mb-1">Validité (jours)</div>
                <Input type="number" value={settings.birthday_benefit?.validity_days ?? 7} onChange={(e) => setSettings({ ...settings, birthday_benefit: { ...settings.birthday_benefit, validity_days: Number(e.target.value) } })} className="bg-black border-zinc-800" data-testid="setting-benefit-validity" />
              </div>
            </div>
          </div>

          <div className="border-t border-zinc-800 pt-4">
            <div className="text-[10px] text-zinc-500 uppercase mb-1">Samedi parents-enfants (ordinal du mois)</div>
            <select value={settings.family_saturday_ordinal || 2} onChange={(e) => setSettings({ ...settings, family_saturday_ordinal: Number(e.target.value) })} className="w-full bg-black border border-zinc-800 rounded-md text-sm p-2" data-testid="setting-family-ordinal">
              <option value={1}>1er samedi</option>
              <option value={2}>2e samedi</option>
              <option value={3}>3e samedi</option>
              <option value={4}>4e samedi</option>
            </select>
            <div className="text-[10px] text-zinc-600 mt-1">Régénère le calendrier après changement pour appliquer.</div>
          </div>

          <div className="border-t border-zinc-800 pt-4 space-y-3">
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <div className="text-sm font-semibold text-zinc-100">Rappels de fermeture automatiques</div>
                <div className="text-xs text-zinc-500">Email aux parents avant chaque férié/fermeture (cron quotidien 9h00).</div>
              </div>
              <input type="checkbox" checked={!!settings.auto_send_closure_reminders} onChange={(e) => setSettings({ ...settings, auto_send_closure_reminders: e.target.checked })} className="w-5 h-5 accent-amber-500" data-testid="setting-auto-reminders" />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer">
              <div>
                <div className="text-sm text-zinc-200">Aussi par SMS (coûts Twilio)</div>
                <div className="text-xs text-zinc-500">Désactivé par défaut. Active pour envoyer aussi les rappels par SMS.</div>
              </div>
              <input type="checkbox" checked={(settings.closure_reminder_channels || []).includes("sms")} onChange={(e) => {
                const has = (settings.closure_reminder_channels || []).includes("sms");
                let ch = settings.closure_reminder_channels || ["email"];
                ch = e.target.checked && !has ? [...ch, "sms"] : ch.filter((c) => c !== "sms");
                if (!ch.includes("email")) ch.push("email");
                setSettings({ ...settings, closure_reminder_channels: ch });
              }} className="w-5 h-5 accent-amber-500" data-testid="setting-reminder-sms" />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer pt-1">
              <div>
                <div className="text-sm font-semibold text-zinc-100">Snapshots annuels en pilote automatique</div>
                <div className="text-xs text-zinc-500">Email auto aux parents : Rentrée (15 août) · Mi-année (1 déc) · Été (1 mai).</div>
              </div>
              <input type="checkbox" checked={settings.auto_publish_snapshots !== false} onChange={(e) => setSettings({ ...settings, auto_publish_snapshots: e.target.checked })} className="w-5 h-5 accent-amber-500" data-testid="setting-auto-snapshots" />
            </label>
            <label className="flex items-center justify-between gap-3 cursor-pointer pt-1">
              <div>
                <div className="text-sm font-semibold text-zinc-100">Cross-post auto sur Facebook</div>
                <div className="text-xs text-zinc-500">Publie aussi le snapshot sur la Page Facebook (texte). Instagram reste manuel (image requise).</div>
              </div>
              <input type="checkbox" checked={settings.auto_publish_snapshots_facebook !== false} onChange={(e) => setSettings({ ...settings, auto_publish_snapshots_facebook: e.target.checked })} className="w-5 h-5 accent-amber-500" data-testid="setting-auto-snapshots-fb" />
            </label>
          </div>

          <Button onClick={saveSettings} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-zinc-950" data-testid="save-settings-btn">
            {busy ? "…" : "💾 Enregistrer les réglages"}
          </Button>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${active ? "border-amber-500 text-amber-300" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
      <div className="text-2xl">{icon}</div>
      <div className="text-2xl font-bold text-zinc-100 mt-1">{value ?? 0}</div>
      <div className="text-[10px] tracking-[0.2em] text-zinc-500 uppercase">{label}</div>
    </div>
  );
}
